import { useState } from 'react';
import {
  ArrowRight,
  ShieldCheck,
  Users,
  TrendingDown,
  Clock,
  EyeOff,
  Search,
  Award,
  Target,
  ClipboardCheck,
  Lock,
  MapPin,
  Leaf,
  Check,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import TreeQuickForm from '../components/TreeQuickForm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_270b8cc9-1693-407c-b15e-5414d2b83f2e/artifacts/qe9vta95_LOGO-FIRMA-PNG-1-branco-258x278-1%20%282%29.png";

// Residential tree-service photography (Unsplash — free commercial license).
// ponytail: hotlinked from the Unsplash CDN. Swap for the client's own crew shots when available.
const IMG_HERO = "https://images.unsplash.com/photo-1754322449185-31f56117ed87?auto=format&fit=crop&w=1600&q=70";
const IMG_RESIDENTIAL = "https://images.unsplash.com/photo-1754321889123-0485c7fea5f1?auto=format&fit=crop&w=1200&q=75";
const IMG_URBAN = "https://images.unsplash.com/photo-1657730391002-bf55ff069a80?auto=format&fit=crop&w=900&q=75";

// Decorative botanical branch — soft, low-opacity organic accent for section corners.
function LeafDecor({ className = '' }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" className={className} aria-hidden="true">
      <path d="M100 190C100 120 60 70 20 45" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M70 120c-18-6-30-2-46 8 12 14 26 18 44 12" fill="currentColor" opacity="0.5" />
      <path d="M60 95c-16-8-28-6-44 2 10 15 24 20 42 16" fill="currentColor" opacity="0.4" />
      <path d="M52 70c-15-9-27-9-42-3 9 16 22 22 40 20" fill="currentColor" opacity="0.35" />
      <path d="M100 190C100 120 140 70 180 45" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
      <path d="M130 120c18-6 30-2 46 8-12 14-26 18-44 12" fill="currentColor" opacity="0.35" />
      <path d="M140 95c16-8 28-6 44 2-10 15-24 20-42 16" fill="currentColor" opacity="0.28" />
    </svg>
  );
}

// Tree-service landing page — warm editorial layout in a moss/forest palette
// (Tree Leads Today + china-guide references). Serif display headlines, real
// tree photography, soft rounded cards, botanical accents. Embedded 2-step
// "territory" quick form. Served at /tree-service.
export default function TreeServicePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollTo = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };
  const goToForm = () => scrollTo('lead-form');

  const navItems = [
    { label: 'System', id: 'system' },
    { label: 'Guarantee', id: 'guarantee' },
    { label: 'Territory', id: 'cta' },
  ];

  const heroChips = [
    { icon: ShieldCheck, text: 'One company per territory' },
    { icon: Users, text: 'No competing clients' },
    { icon: Leaf, text: 'No shared leads' },
  ];

  const traps = [
    { icon: TrendingDown, title: 'Costly Downtime', text: 'Your bucket truck never stops costing money when the phone slows down. Every idle day is profit you will never get back.' },
    { icon: Users, title: 'The Race to the Bottom', text: 'Shared leads put you in a bidding war. Homeowners shop on price, squeezing your margins for less reward.' },
    { icon: Clock, title: 'Wasted Capacity', text: 'Tire-kickers waste your gas and your estimator\'s time — driving across town for a job that was never going to close.' },
    { icon: EyeOff, title: 'Invisible Reputation', text: 'Hundreds of five-star reviews won\'t fill your calendar if homeowners never see you first when an emergency hits.' },
  ];

  const steps = [
    { icon: Search, step: '01', title: 'Territory Search Positioning', text: 'We place you where high-intent customers already search — Google Search, Maps, emergency and commercial intent.' },
    { icon: Award, step: '02', title: 'Technical Authority Positioning', text: 'Messaging built for professional tree operators. Not generic contractor marketing.' },
    { icon: Target, step: '03', title: 'High-Intent Opportunity Capture', text: 'Removals, hazardous work, storm cleanup, commercial projects. Only buying intent — no wasted traffic.' },
    { icon: ClipboardCheck, step: '04', title: 'Estimate Qualification', text: 'Every opportunity is reviewed before it reaches your office. Qualified owner, real project, estimate-ready.' },
    { icon: Lock, step: '05', title: 'Territory Lock™', text: 'One territory. One partner. No shared leads. No internal competition. Ever.' },
  ];

  const results = ['Higher-value projects', 'More emergency work', 'Better close rates', 'Stronger market authority', 'Predictable growth'];

  return (
    <div className="min-h-screen bg-[#F6F3EC] font-sans text-[#26331F]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#16241A] border-b border-[#26402A]" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5" data-testid="logo">
              <img src={LOGO_URL} alt="The Tree Lead System" className="h-9 w-auto" />
              <span className="hidden sm:block text-sm font-bold tracking-wide text-white uppercase">Tree Lead System</span>
            </div>

            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navItems.map((item) => (
                <button key={item.id} onClick={() => scrollTo(item.id)} className="text-xs font-semibold uppercase tracking-wider text-[#A9C97E] hover:text-white transition-colors" data-testid={`nav-${item.id}`}>
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:block">
              <Button onClick={goToForm} className="btn-moss !py-2.5 !px-5 text-xs uppercase tracking-wider" data-testid="header-cta">
                Check If Your City Is Available
              </Button>
            </div>

            <button className="lg:hidden p-2 text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#16241A] border-t border-[#26402A] px-4 py-6 space-y-4" data-testid="mobile-menu">
            {navItems.map((item) => (
              <button key={item.id} onClick={() => scrollTo(item.id)} className="block w-full text-left text-sm font-semibold uppercase tracking-wider text-[#A9C97E]" data-testid={`mobile-nav-${item.id}`}>
                {item.label}
              </button>
            ))}
            <Button onClick={goToForm} className="btn-moss w-full text-xs uppercase tracking-wider">Check If Your City Is Available</Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16241A]" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(18,32,22,0.82) 0%, rgba(20,36,25,0.88) 55%, rgba(14,26,18,0.94) 100%)' }} aria-hidden="true" />
        <LeafDecor className="pointer-events-none absolute -top-6 -left-8 w-56 h-56 text-[#3E5A2C] opacity-40 rotate-12" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 border border-[#4A6A2A] bg-[#1D3020]/60 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#C7DEA1] mb-6" data-testid="hero-badge">
                <Leaf className="w-3.5 h-3.5" /> For Professional Tree Companies
              </span>

              <h1 className="font-display font-black leading-[1.02] tracking-tight text-white text-4xl sm:text-5xl lg:text-[3.5rem] mb-6" data-testid="hero-headline">
                Generate <span className="text-[#A9C97E]">30–80 Qualified</span> Tree Removal &amp; Emergency Jobs Every Month
              </h1>

              <p className="font-display italic text-lg sm:text-xl font-medium text-[#EAF0DC] mb-4" data-testid="hero-guarantee">
                If we don't deliver 30–80 qualified estimates in 90 days, you don't pay.
              </p>
              <p className="text-base text-[#B7C4A6] mb-8 max-w-lg mx-auto lg:mx-0" data-testid="hero-sub">
                A customer acquisition system engineered for the tree industry — protected by Territory Lock™. One company per territory. No exceptions.
              </p>

              <Button onClick={goToForm} className="btn-moss uppercase tracking-wider" data-testid="hero-cta">
                Check If Your City Is Available <ArrowRight className="w-5 h-5" />
              </Button>

              <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-8" data-testid="hero-chips">
                {heroChips.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 border border-[#3A5528] bg-[#1B2E1E]/60 rounded-full px-4 py-2 text-xs text-[#C7DEA1]">
                    <c.icon className="w-4 h-4 text-[#A9C97E]" /> {c.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="flex justify-center lg:justify-end" data-testid="hero-form">
              <TreeQuickForm id="lead-form" />
            </div>
          </div>
        </div>
      </section>

      {/* Industry Traps */}
      <section className="relative py-20 lg:py-28 bg-[#F6F3EC]" data-testid="traps-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow-moss mb-3"><span className="inline-block w-6 h-px bg-[#5E7D3A]" /> The Real Problem</p>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-[#22301F] mb-3 leading-tight" data-testid="traps-headline">
            Great Tree Companies Lose Jobs <span className="text-[#5E7D3A]">Every Day.</span>
          </h2>
          <p className="text-lg text-[#6B6558] mb-12 max-w-2xl">
            Not because they're less qualified — because they're less visible. Homeowners hire the company they find first.
          </p>

          <div className="grid sm:grid-cols-2 gap-5" data-testid="trap-cards">
            {traps.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E7E0D2] p-6 shadow-[0_10px_30px_-20px_rgba(34,48,31,0.4)]" data-testid={`trap-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#EAF0DC] flex items-center justify-center mb-4">
                  <t.icon className="w-5 h-5 text-[#5E7D3A]" />
                </div>
                <h3 className="text-lg font-bold text-[#22301F] mb-2">{t.title}</h3>
                <p className="text-sm text-[#6B6558] leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Authority band (photo) */}
      <section className="relative overflow-hidden bg-[#16241A]" data-testid="authority-section">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[280px] lg:min-h-full">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_RESIDENTIAL})` }} aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#16241A] lg:to-[#16241A]/95" aria-hidden="true" />
          </div>
          <div className="relative px-4 sm:px-6 lg:px-12 py-16 lg:py-24">
            <LeafDecor className="pointer-events-none absolute top-4 right-2 w-40 h-40 text-[#2E4620] opacity-50 -rotate-12" />
            <p className="eyebrow-light mb-3"><span className="inline-block w-6 h-px bg-[#A9C97E]" /> Built For Tree Professionals</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-white mb-5 leading-tight">
              Not generic contractor marketing. A system that speaks <span className="text-[#A9C97E]">arborist</span>.
            </h2>
            <p className="text-[#B7C4A6] text-lg mb-8 max-w-md">
              Removals, hazardous work, storm cleanup, commercial contracts — positioned to the homeowners and property managers already looking for a professional crew.
            </p>
            <div className="space-y-3">
              {['One company per territory — locked', 'Only real, estimate-ready projects', 'You own your market, not a shared list'].map((line, i) => (
                <div key={i} className="flex items-center gap-3 text-[#EAF0DC]">
                  <span className="w-6 h-6 rounded-full bg-[#5E7D3A] flex items-center justify-center flex-shrink-0"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-sm sm:text-base">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* System / How It Works */}
      <section id="system" className="relative py-20 lg:py-28 bg-[#ECF0E2]" data-testid="system-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <p className="eyebrow-moss mb-3"><span className="inline-block w-6 h-px bg-[#5E7D3A]" /> The System</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black text-[#22301F] mb-4 leading-tight" data-testid="system-headline">
                This isn't lead gen. It's <span className="text-[#5E7D3A]">acquisition infrastructure.</span>
              </h2>
              <p className="text-lg text-[#6B6558] mb-6">
                Five components that turn buying intent into estimate-ready customers — deployed one company per territory.
              </p>
              <div className="relative rounded-2xl overflow-hidden hidden lg:block shadow-[0_25px_50px_-20px_rgba(34,48,31,0.5)]">
                <img src={IMG_URBAN} alt="Arborist pruning a street tree" className="w-full h-72 object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#16241A]/70 to-transparent" />
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5" data-testid="system-steps">
              {steps.map((s, i) => (
                <div key={i} className="bg-white border border-[#E1E5D4] rounded-2xl p-6 shadow-[0_10px_30px_-22px_rgba(34,48,31,0.45)]" data-testid={`step-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-[#EAF0DC] flex items-center justify-center">
                      <s.icon className="w-5 h-5 text-[#5E7D3A]" />
                    </div>
                    <span className="font-display text-3xl font-black text-[#D8E3C6]">{s.step}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#22301F] mb-2">{s.title}</h3>
                  <p className="text-sm text-[#6B6558] leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee / Expected Results */}
      <section id="guarantee" className="relative py-20 lg:py-28 bg-[#F6F3EC] overflow-hidden" data-testid="guarantee-section">
        <LeafDecor className="pointer-events-none absolute -bottom-10 -right-10 w-64 h-64 text-[#DCE4CC] rotate-180" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#EAF0DC] flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-8 h-8 text-[#5E7D3A]" />
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-[#22301F] mb-4" data-testid="guarantee-headline">
            30–80 Qualified Estimates. <span className="text-[#5E7D3A]">Guaranteed.</span>
          </h2>
          <p className="text-lg text-[#6B6558] mb-10 max-w-2xl mx-auto">
            If the Tree Lead System™ doesn't deliver 30–80 qualified estimates in your first 90 days, we refund every dollar you've invested. Clear accountability. No excuses.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="results">
            {results.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-2 bg-white border border-[#E7E0D2] rounded-full px-4 py-2 text-sm font-medium text-[#22301F]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#5E7D3A]" /> {r}
              </span>
            ))}
          </div>

          <Button onClick={goToForm} className="btn-moss uppercase tracking-wider mx-auto" data-testid="guarantee-cta">
            Check If Your City Is Available <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="relative overflow-hidden bg-[#16241A]" data-testid="cta-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${IMG_HERO})` }} aria-hidden="true" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 100% at 50% 100%, rgba(30,50,32,0.7) 0%, rgba(14,26,18,0.96) 65%)' }} aria-hidden="true" />
        <LeafDecor className="pointer-events-none absolute -bottom-8 -left-8 w-56 h-56 text-[#2E4620] opacity-50" />
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#1D3020] border border-[#3A5528] flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-7 h-7 text-[#A9C97E]" />
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black text-white mb-5" data-testid="cta-headline">
            Is Your Territory <span className="text-[#A9C97E]">Still Available?</span>
          </h2>
          <p className="text-lg text-[#B7C4A6] mb-8">
            One tree service company per territory. Once it's secured, it's permanently closed. Secure your market before another company does.
          </p>
          <Button onClick={goToForm} className="btn-moss uppercase tracking-wider mx-auto" data-testid="cta-button">
            Check If Your City Is Available <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1A12] py-10 border-t border-[#26402A]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="The Tree Lead System" className="h-8 w-auto" />
            <span className="text-sm text-[#A9C97E]">The Tree Lead System™</span>
          </div>
          <p className="text-xs text-[#7C8B6E]">© {new Date().getFullYear()} The Tree Lead System™. One company per territory.</p>
        </div>
      </footer>
    </div>
  );
}
